<template>
  <section
    :class="{
      'osk-about-service': true,
      'osk-about-service_aside': aside,
    }"
  >
    <div class="osk-wrapper">
      <h2
        v-if="!aside"
        class="osk-about-service__title"
      >
        {{ $t('components.business.aboutService.aboutService.title') }}
      </h2>

      <UikitCollapse
        :class="{
          'osk-about-service__content': true,
          'osk-about-service__content_aside': aside,
        }"
        :disable="desktopScreen"
      >
        <UikitCollapseItem
          :title="$t('components.business.aboutService.aboutService.qualityControl.title')"
          icon="Shield"
        >
          <div
            v-if="desktopScreen"
            class="osk-about-service__text"
          >
            {{ $t('components.business.aboutService.aboutService.qualityControl.text') }}
            <span
              class="osk-link"
              @click="openDialog(ECartInfo.Quality)"
            >
              {{ $t('common.moreDetails') }}
            </span>
          </div>

          <BusinessAboutServiceContent
            v-else
            :active="ECartInfo.Quality"
          />
        </UikitCollapseItem>

        <UikitCollapseItem
          :title="$t('components.business.aboutService.aboutService.delivery.title')"
          icon="Box"
        >
          <div
            v-if="desktopScreen"
            class="osk-about-service__text"
          >
            {{ $t('components.business.aboutService.aboutService.delivery.text') }}
            <span
              class="osk-link"
              @click="openDialog(ECartInfo.Delivery)"
            >
              {{ $t('common.moreDetails') }}
            </span>
          </div>

          <BusinessAboutServiceContent
            v-else
            :active="ECartInfo.Delivery"
          />
        </UikitCollapseItem>

        <UikitCollapseItem
          v-if="aside"
          :title="$t('components.business.aboutService.aboutService.return.title')"
          icon="Return"
        >
          <div
            v-if="desktopScreen"
            class="osk-about-service__text"
          >
            {{ $t('components.business.aboutService.aboutService.return.text') }}
            <span
              class="osk-link"
              @click="openDialog(ECartInfo.Return)"
            >
              {{ $t('common.moreDetails') }}
            </span>
          </div>

          <BusinessAboutServiceContent
            v-else
            :active="ECartInfo.Return"
          />
        </UikitCollapseItem>

        <UikitCollapseItem
          :title="$t('components.business.aboutService.aboutService.security.title')"
          icon="Card"
        >
          <div
            v-if="desktopScreen"
            class="osk-about-service__text"
          >
            {{ $t('components.business.aboutService.aboutService.security.text') }}
            <span
              class="osk-link"
              @click="openDialog(ECartInfo.Security)"
            >
              {{ $t('common.moreDetails') }}
            </span>
          </div>

          <BusinessAboutServiceContent
            v-else
            :active="ECartInfo.Security"
          />
        </UikitCollapseItem>

        <UikitCollapseItem
          :title="$t('components.business.aboutService.aboutService.help.title')"
          icon="Phone"
        >
          <div
            v-if="desktopScreen"
            class="osk-about-service__text"
          >
            {{ $t('components.business.aboutService.aboutService.help.text') }}
            <span
              class="osk-link"
              @click="openDialog(ECartInfo.Help)"
            >
              {{ $t('common.moreDetails') }}
            </span>
          </div>

          <BusinessAboutServiceContent
            v-else
            :active="ECartInfo.Help"
          />
        </UikitCollapseItem>
      </UikitCollapse>
    </div>

    <UikitDialog
      v-model="isDialogOpen"
      position="right"
      full-height
      no-padding
      content-class="osk-about-service__dialog"
    >
      <div class="osk-about-service__dialog-content">
        <div class="osk-about-service__dialog-info">
          <div class="osk-about-service__dialog-title">
            <template v-if="infoContent === ECartInfo.Quality">
              {{ $t('components.business.aboutService.aboutService.quality.title') }}
            </template>

            <template v-if="infoContent === ECartInfo.Delivery">
              {{ $t('components.business.aboutService.aboutService.delivery.title') }}
            </template>

            <template v-if="infoContent === ECartInfo.Return">
              {{ $t('components.business.aboutService.aboutService.return.title') }}
            </template>

            <template v-if="infoContent === ECartInfo.Security">
              {{ $t('components.business.aboutService.aboutService.security.title') }}
            </template>

            <template v-if="infoContent === ECartInfo.Help">
              {{ $t('components.business.aboutService.aboutService.help.title') }}
            </template>
          </div>

          <BusinessAboutServiceContent :active="infoContent" />
        </div>

        <div class="osk-about-service__dialog-footer">
          <UikitButton
            full-width
            @click="isDialogOpen = false"
          >
            {{ $t('common.itsClear') }}
          </UikitButton>
        </div>
      </div>
    </UikitDialog>
  </section>
</template>

<script setup lang="ts">
import { ECartInfo } from '~/types/cart';
import { AnalyticsEvents } from '~/types/analytics';

const props = defineProps<{
  page?: string
  aside?: boolean
}>();

const { mobile, laptopS, isMounted } = useUiBreakpoints();
const { $addEvent } = useNuxtApp();

const isDialogOpen = ref(false);
const infoContent = ref<ECartInfo>(ECartInfo.Quality);

const desktopScreen = computed(() => (isMounted.value && !(mobile.value || laptopS.value)));

function openDialog(info: ECartInfo) {
  infoContent.value = info;
  isDialogOpen.value = true;

  $addEvent(AnalyticsEvents.AboutServiceClick, {
    info,
    page: props.page,
  });
}
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-about-service {
  padding: $indent-large 0;

  @include media-query(md-and-up) {
    padding: $indent-xlarge 0 $indent-xxlarge;
  }

  &_aside {
    padding: 0;

    .osk-wrapper {
      padding: 0;
    }
  }

  &__title {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    padding: $indent-small 0;

    @include media-query(md-and-up) {
      text-transform: uppercase;
      text-align: center;
      padding: 0;
      margin-bottom: $indent-mlarge;
    }
  }

  &__content {
    @include media-query(md-and-up) {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;

      .osk-collapse-item {
        max-width: 50%;
        width: 100%;
        padding: 0 12px 20px;

        @include media-query(lg-and-up) {
          max-width: 100%;
        }
      }

      .osk-collapse-item__title {
        flex-direction: column;
        align-items: flex-start;
        padding-bottom: $indent-small;
      }

      .osk-collapse-item__title-text {
        margin-top: $indent-mmedium;
        line-height: $line-height-subheading;
        font-weight: $font-weight-bold;
      }

      &:not(.osk-about-service__content_aside) {
        border-radius: $radius-small;
        box-shadow: $card-shadow;

        .osk-collapse-item {
          padding: $indent-xlarge;

          @include media-query(lg-and-up) {
            max-width: 25%;
          }
        }
      }
    }

    &_aside {
      .osk-collapse-item {
        &:last-child {
          margin-bottom: 16px;
        }

        @include media-query(lg-and-up) {
          padding-left: 54px;
          position: relative;

          .osk-collapse-item__title-icon {
            position: absolute;
            top: 10px;
            left: 0;
            width: 28px;
            height: 28px;
            margin-right: 0;
          }

          .osk-collapse-item__title-text {
            margin-top: 0;
          }
        }
      }
    }
  }

  &__text {
    line-height: $line-height-subheading;
  }

  &__dialog-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-top: $indent-xlarge;
  }

  &__dialog-info {
    max-width: 504px;
    overflow-y: auto;
    height: 100%;
    padding: 0 $indent-mlarge;

    @include media-query(md-and-up) {
      padding: 0 52px;
      padding-top: 60px;
    }

    .osk-about-service-content {
      padding: 0;
    }

    .osk-about-service-content__help-text {
      width: 100%;
    }

    .osk-about-service-content__help-wrap {
      flex-direction: column;
      align-items: flex-start;
      margin-top: $indent-medium;
    }

    .osk-about-service-content__col {
      width: 100%;

      &:last-child {
        padding: 0;
      }
    }
  }

  &__dialog-footer {
    padding: $indent-medium $indent-mlarge $indent-mmedium;

    @include media-query(md-and-up) {
      padding: $indent-medium 52px $indent-mmedium;
    }
  }

  &__dialog {
    .osk-dialog__close {
      display: none;

      @include media-query(md-and-up) {
        display: block;
      }

      @include media-query(lg-and-up) {
        top: 44px;
        right: 44px;
      }
    }
  }

  &__dialog-title {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    margin-bottom: $indent-mlarge;
  }
}
</style>
